import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, TextInput,
  Platform, Dimensions, ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '../context/NavigationContext';
import Constants from 'expo-constants';
import {
  QUERY_TYPES, SPORTS_LEAGUES, SPORTSBOOK_TIERS, SYNTAX_PATTERNS, MARKET_SHORTCUTS,
  EXAMPLES_GALLERY, GLOSSARY_TERMS, MARKET_DESCRIPTIONS,
} from '../constants/docsData';
import { styles } from './docsScreenStyles';

const getFunctionsBase = () => (
  (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
  || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
);

const TABS = ['How It Works', 'Query Builder', 'Markets', 'Players & Teams', 'Books', 'Glossary'];
const TAB_ICONS = ['book-outline', 'construct-outline', 'grid-outline', 'people-outline', 'business-outline', 'library-outline'];

// ─── Live Query Tokenizer ─────────────────────────────────────────────────────
// Annotates each word in a query with its detected entity type.
// Used by LiveQueryAnatomy to show color-coded chips in real time.
const STOP_WORDS = new Set(['tonight', 'today', 'and', 'or', 'vs', 'v', 'the', 'a', 'an', 'bets', 'bet', 'game', 'games', 'on', 'at', 'in', 'of', 'for', 'my', 'all', 'with', 'by', 'picks', 'pick']);

const MARKET_KW_MAP = {};
MARKET_SHORTCUTS.forEach(shortcut => {
  shortcut.keyword.split('/').forEach(kw => {
    const k = kw.trim().toLowerCase();
    if (k.length >= 2) MARKET_KW_MAP[k] = shortcut.resolves;
  });
});

const LEAGUE_MAP = {};
SPORTS_LEAGUES.forEach(sport => {
  sport.leagues.forEach(league => {
    LEAGUE_MAP[league.id.toLowerCase()] = { label: league.label, color: sport.color, sport: sport.sport };
    LEAGUE_MAP[league.label.toLowerCase()] = { label: league.label, color: sport.color, sport: sport.sport };
  });
  LEAGUE_MAP[sport.sport.toLowerCase()] = { label: sport.sport, color: sport.color, sport: sport.sport };
});

const BOOK_MAP = {};
SPORTSBOOK_TIERS.forEach(tier => {
  tier.books.forEach(book => {
    BOOK_MAP[book.label.toLowerCase()] = book.label;
    BOOK_MAP[book.id.toLowerCase().replace(/_/g, '')] = book.label;
    book.label.split(/\s+/).forEach(w => {
      if (w.length > 4) BOOK_MAP[w.toLowerCase()] = book.label;
    });
  });
});

function tokenizeQuery(rawQuery) {
  if (!rawQuery.trim()) return [];
  const tokens = rawQuery.trim().split(/\s+/);
  const annotated = [];
  let i = 0;

  while (i < tokens.length) {
    const token = tokens[i];
    const t = token.toLowerCase().replace(/[+?.,!*]/g, '');
    const t2 = i + 1 < tokens.length ? `${t} ${tokens[i + 1].toLowerCase().replace(/[+?,!]/g, '')}` : '';
    const t3 = t2 && i + 2 < tokens.length ? `${t2} ${tokens[i + 2].toLowerCase().replace(/[+?,!]/g, '')}` : '';

    // Mode (multi-word first)
    if (t3 === 'same game parlay') { annotated.push({ text: tokens.slice(i, i + 3).join(' '), type: 'Mode', color: '#a78bfa', typeLabel: 'Mode: SGP' }); i += 3; continue; }
    if (t2 === 'best bets' || t2 === 'best picks') { annotated.push({ text: tokens.slice(i, i + 2).join(' '), type: 'Mode', color: '#fbbf24', typeLabel: 'Mode: Best Bets' }); i += 2; continue; }
    if (t === 'sgp' || t === 'parlay') { annotated.push({ text: token, type: 'Mode', color: '#a78bfa', typeLabel: 'Mode: SGP' }); i++; continue; }
    if (t === 'live') { annotated.push({ text: token, type: 'Mode', color: '#f87171', typeLabel: 'Mode: Live' }); i++; continue; }
    if (t === 'futures' || t === 'future') { annotated.push({ text: token, type: 'Mode', color: '#fb923c', typeLabel: 'Mode: Futures' }); i++; continue; }

    // Stopwords
    if (STOP_WORDS.has(t)) { annotated.push({ text: token, type: 'stop', color: 'rgba(255,255,255,0.2)', typeLabel: null }); i++; continue; }

    // Direction
    if (t === 'over' || t === 'o/') { annotated.push({ text: token, type: 'Direction', color: '#4ade80', typeLabel: 'Direction: Over' }); i++; continue; }
    if (t === 'under' || t === 'u/') { annotated.push({ text: token, type: 'Direction', color: '#f87171', typeLabel: 'Direction: Under' }); i++; continue; }

    // Line value
    if (/^\d+(\.\d+)?\+?$/.test(t) || /^[-+]\d+(\.\d+)?$/.test(t)) { annotated.push({ text: token, type: 'Line', color: '#fbbf24', typeLabel: 'Line value' }); i++; continue; }

    // League (2-word first)
    if (t2 && LEAGUE_MAP[t2]) { const l = LEAGUE_MAP[t2]; annotated.push({ text: tokens.slice(i, i + 2).join(' '), type: 'League', color: l.color, typeLabel: `Sport: ${l.sport}` }); i += 2; continue; }
    if (LEAGUE_MAP[t]) { const l = LEAGUE_MAP[t]; annotated.push({ text: token, type: 'League', color: l.color, typeLabel: `Sport: ${l.sport}` }); i++; continue; }

    // Market (2-word first)
    if (t2 && MARKET_KW_MAP[t2]) { annotated.push({ text: tokens.slice(i, i + 2).join(' '), type: 'Market', color: '#fbbf24', typeLabel: `Market: ${MARKET_KW_MAP[t2]}` }); i += 2; continue; }
    if (MARKET_KW_MAP[t]) { annotated.push({ text: token, type: 'Market', color: '#fbbf24', typeLabel: `Market: ${MARKET_KW_MAP[t]}` }); i++; continue; }

    // Sportsbook
    if (t2 && BOOK_MAP[t2]) { annotated.push({ text: tokens.slice(i, i + 2).join(' '), type: 'Sportsbook', color: '#34d399', typeLabel: `Book: ${BOOK_MAP[t2]}` }); i += 2; continue; }
    if (BOOK_MAP[t]) { annotated.push({ text: token, type: 'Sportsbook', color: '#34d399', typeLabel: `Book: ${BOOK_MAP[t]}` }); i++; continue; }

    // Default: team / player entity
    annotated.push({ text: token, type: 'Entity', color: 'rgba(255,255,255,0.85)', typeLabel: 'Team / Player' });
    i++;
  }

  return annotated.filter(t => t.text.trim());
}

const ENTITY_TYPE_META = {
  Mode:       { color: '#a78bfa', icon: 'git-merge-outline' },
  Direction:  { color: '#4ade80', icon: 'arrow-up-outline' },
  Line:       { color: '#fbbf24', icon: 'remove-outline' },
  League:     { color: '#60a5fa', icon: 'trophy-outline' },
  Market:     { color: '#fbbf24', icon: 'grid-outline' },
  Sportsbook: { color: '#34d399', icon: 'business-outline' },
  Entity:     { color: 'rgba(255,255,255,0.85)', icon: 'person-outline' },
};

// ─── Components ───────────────────────────────────────────────────────────────

function LiveQueryAnatomy({ onRunQuery }) {
  const [input, setInput] = useState('');
  const tokens = useMemo(() => tokenizeQuery(input), [input]);
  const EXAMPLES = ['lebron 25+ points sgp', 'mahomes over 300 passing yards', 'fanduel best nba bets tonight', 'celtics vs bucks over 220', 'live chiefs spread'];

  return (
    <View style={styles.anatomyCard}>
      <Text style={styles.anatomyTitle}>Live Query Anatomy</Text>
      <Text style={styles.anatomySubtitle}>Type any query to see how the system detects each entity in real time.</Text>
      <TextInput
        style={styles.anatomyInput}
        value={input}
        onChangeText={setInput}
        placeholder="e.g. lebron 25+ points sgp"
        placeholderTextColor="rgba(255,255,255,0.25)"
        autoCapitalize="none"
        autoCorrect={false}
      />
      {/* Quick examples */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 8 }} contentContainerStyle={{ gap: 6 }}>
        {EXAMPLES.map((ex, i) => (
          <TouchableOpacity key={i} onPress={() => setInput(ex)} style={styles.anatomyExPill}>
            <Text style={styles.anatomyExPillText}>{ex}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {tokens.length > 0 && (
        <>
          <View style={styles.anatomyTokenRow}>
            {tokens.map((tok, i) => (
              tok.type === 'stop' ? null : (
                <View key={i} style={[styles.anatomyToken, { borderColor: tok.color + '66', backgroundColor: tok.color + '18' }]}>
                  <Text style={[styles.anatomyTokenText, { color: tok.color }]}>{tok.text}</Text>
                  {tok.typeLabel && <Text style={[styles.anatomyTokenLabel, { color: tok.color + 'aa' }]}>{tok.typeLabel}</Text>}
                </View>
              )
            ))}
          </View>
          {/* Legend */}
          <View style={styles.anatomyLegend}>
            {Object.entries(ENTITY_TYPE_META).map(([type, meta]) => (
              <View key={type} style={styles.anatomyLegendItem}>
                <View style={[styles.anatomyLegendDot, { backgroundColor: meta.color }]} />
                <Text style={styles.anatomyLegendText}>{type}</Text>
              </View>
            ))}
          </View>
          {input.trim() && (
            <TouchableOpacity onPress={() => onRunQuery(input.trim())} style={styles.anatomyRunBtn}>
              <Text style={styles.anatomyRunBtnText}>Run this query</Text>
              <Ionicons name="arrow-forward-circle" size={15} color="#0f0f17" style={{ marginLeft: 5 }} />
            </TouchableOpacity>
          )}
        </>
      )}
    </View>
  );
}

function ExamplesGallery({ onRunQuery }) {
  const [expanded, setExpanded] = useState(null);

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Examples Gallery</Text>
      <Text style={styles.sectionSubtitle}>Curated queries for common use cases. Tap any query to run it.</Text>
      <View style={{ gap: 8 }}>
        {EXAMPLES_GALLERY.map(group => {
          const isOpen = expanded === group.id;
          return (
            <View key={group.id} style={[styles.exGalleryCard, { borderLeftColor: group.color }]}>
              <TouchableOpacity onPress={() => setExpanded(isOpen ? null : group.id)} style={styles.exGalleryHeader} activeOpacity={0.85}>
                <View style={[styles.exGalleryIconWrap, { backgroundColor: group.color + '22' }]}>
                  <Ionicons name={group.icon} size={16} color={group.color} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.exGalleryLabel, { color: group.color }]}>{group.label}</Text>
                  <Text style={styles.exGalleryDesc}>{group.description}</Text>
                </View>
                <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={15} color="rgba(255,255,255,0.3)" />
              </TouchableOpacity>
              {isOpen && (
                <View style={{ paddingTop: 8, gap: 6 }}>
                  {group.queries.map((q, qi) => (
                    <TouchableOpacity key={qi} onPress={() => onRunQuery(q.query)} style={styles.exGalleryRow} activeOpacity={0.8}>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.exGalleryQLabel}>{q.label}</Text>
                        <Text style={[styles.exGalleryQQuery, { color: group.color }]}>{q.query}</Text>
                      </View>
                      <View style={[styles.runBtn, { backgroundColor: group.color + '22', borderColor: group.color + '55' }]}>
                        <Text style={[styles.runBtnText, { color: group.color }]}>Try</Text>
                        <Ionicons name="arrow-forward-circle" size={13} color={group.color} style={{ marginLeft: 2 }} />
                      </View>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>
          );
        })}
      </View>
    </View>
  );
}

// ─── Pill button ──────────────────────────────────────────────────────────────
function Pill({ label, active, onPress, color = '#a78bfa', small }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={[styles.pill, active && { borderColor: color, backgroundColor: color + '22' }, small && { paddingVertical: 4, paddingHorizontal: 8 }]}
    >
      <Text style={[styles.pillText, active && { color }, small && { fontSize: 11 }]}>{label}</Text>
    </TouchableOpacity>
  );
}

// ─── Tab 1: How It Works ──────────────────────────────────────────────────────
function HowItWorksTab({ onRunQuery }) {
  const [expanded, setExpanded] = useState(null);

  return (
    <ScrollView style={styles.tabContent} contentContainerStyle={{ paddingBottom: 60 }} showsVerticalScrollIndicator={false}>
      {/* Intro */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Natural Language Queries</Text>
        <Text style={styles.sectionSubtitle}>
          Type searches the same way you'd say them out loud. The system automatically detects teams, players, markets, lines, directions, and sportsbooks from your query.
        </Text>
      </View>

      {/* Live query anatomy */}
      <LiveQueryAnatomy onRunQuery={onRunQuery} />

      {/* Examples gallery */}
      <ExamplesGallery onRunQuery={onRunQuery} />

      {/* Quick syntax reference */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Syntax Patterns</Text>
        <View style={styles.tableCard}>
          {SYNTAX_PATTERNS.map((p, i) => (
            <View key={i} style={[styles.tableRow, i < SYNTAX_PATTERNS.length - 1 && styles.tableRowBorder]}>
              <View style={{ flex: 1 }}>
                <Text style={styles.tablePattern}>{p.pattern}</Text>
                <Text style={styles.tableDesc}>{p.description}</Text>
              </View>
              <TouchableOpacity onPress={() => onRunQuery(p.example)} style={styles.tryBtn}>
                <Text style={styles.tryBtnText}>{p.example}</Text>
                <Ionicons name="play-circle-outline" size={14} color="#a78bfa" style={{ marginLeft: 4 }} />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      </View>

      {/* Market keyword shortcuts */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Market Shortcuts</Text>
        <Text style={styles.sectionSubtitle}>These keywords are recognized and automatically mapped to their full market names.</Text>
        <View style={styles.tableCard}>
          {MARKET_SHORTCUTS.map((m, i) => (
            <View key={i} style={[styles.tableRow, i < MARKET_SHORTCUTS.length - 1 && styles.tableRowBorder]}>
              <Text style={[styles.tablePattern, { flex: 1 }]}>{m.keyword}</Text>
              <Text style={[styles.tableDesc, { flex: 1, textAlign: 'right' }]}>{m.resolves}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Query type cards */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Query Types</Text>
        <Text style={styles.sectionSubtitle}>The system detects these query types automatically. You don't need to specify the type — just type naturally.</Text>
        {QUERY_TYPES.map((qt) => {
          const isOpen = expanded === qt.id;
          return (
            <TouchableOpacity key={qt.id} onPress={() => setExpanded(isOpen ? null : qt.id)} activeOpacity={0.85}>
              <View style={[styles.queryTypeCard, { borderLeftColor: qt.color }]}>
                <View style={styles.queryTypeHeader}>
                  <View style={[styles.queryTypeIconWrap, { backgroundColor: qt.color + '22' }]}>
                    <Ionicons name={qt.icon} size={18} color={qt.color} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.queryTypeLabel, { color: qt.color }]}>{qt.label}</Text>
                    <Text style={styles.queryTypeDesc} numberOfLines={isOpen ? undefined : 2}>{qt.description}</Text>
                  </View>
                  <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color="rgba(255,255,255,0.4)" />
                </View>

                {isOpen && (
                  <View style={{ paddingTop: 12 }}>
                    {/* Parameters */}
                    <Text style={styles.expandLabel}>Parameters</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                      {qt.parameters.map((p, i) => (
                        <View key={i} style={styles.paramBadge}>
                          <Text style={styles.paramBadgeText}>{p}</Text>
                        </View>
                      ))}
                    </View>
                    {/* Supports */}
                    <Text style={styles.expandLabel}>Supports</Text>
                    <View style={{ gap: 4, marginBottom: 12 }}>
                      {qt.supports.map((s, i) => (
                        <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                          <View style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: qt.color }} />
                          <Text style={styles.supportText}>{s}</Text>
                        </View>
                      ))}
                    </View>
                    {/* Examples */}
                    <Text style={styles.expandLabel}>Examples</Text>
                    <View style={{ gap: 6 }}>
                      {qt.examples.map((ex, i) => (
                        <TouchableOpacity key={i} onPress={() => onRunQuery(ex.query)} style={styles.exampleRow}>
                          <View style={{ flex: 1 }}>
                            <Text style={styles.exampleLabel}>{ex.label}</Text>
                            <Text style={[styles.exampleQuery, { color: qt.color }]}>{ex.query}</Text>
                          </View>
                          <View style={[styles.runBtn, { backgroundColor: qt.color + '22', borderColor: qt.color + '55' }]}>
                            <Text style={[styles.runBtnText, { color: qt.color }]}>Try it</Text>
                            <Ionicons name="arrow-forward-circle" size={14} color={qt.color} style={{ marginLeft: 3 }} />
                          </View>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    </ScrollView>
  );
}

// ─── Tab 2: Query Builder ─────────────────────────────────────────────────────
const EMPTY_LEG = (id) => ({ id, subjectType: 'player', subjectValue: '', market: null, direction: null, lineValue: '', playerSearch: '' });

function LegBuilder({ leg, index, total, players, playersLoading, marketsForSport, onUpdate, onRemove, canRemove }) {
  const filteredPlayers = useMemo(() => {
    if (!leg.playerSearch.trim()) return players.slice(0, 50);
    const q = leg.playerSearch.toLowerCase();
    return players.filter(p => {
      const name = typeof p === 'string' ? p : (p?.player_name || p?.name || '');
      return name.toLowerCase().includes(q);
    }).slice(0, 50);
  }, [players, leg.playerSearch]);

  return (
    <View style={styles.legCard}>
      <View style={styles.legHeader}>
        <View style={[styles.legBadge]}>
          <Text style={styles.legBadgeText}>Leg {index + 1}</Text>
        </View>
        {leg.subjectValue && leg.market && (
          <Text style={styles.legSummary} numberOfLines={1}>{leg.subjectValue} · {leg.market}</Text>
        )}
        {canRemove && (
          <TouchableOpacity onPress={onRemove} style={styles.legRemoveBtn}>
            <Ionicons name="close-circle-outline" size={18} color="rgba(255,255,255,0.35)" />
          </TouchableOpacity>
        )}
      </View>

      {/* Subject toggle */}
      <View style={{ flexDirection: 'row', gap: 6, marginBottom: 8 }}>
        <Pill label="Player" active={leg.subjectType === 'player'} small
          onPress={() => onUpdate({ subjectType: 'player', subjectValue: '', playerSearch: '' })} color="#e879f9" />
        <Pill label="Team" active={leg.subjectType === 'team'} small
          onPress={() => onUpdate({ subjectType: 'team', subjectValue: '' })} color="#34d399" />
      </View>

      {leg.subjectType === 'team' ? (
        <TextInput style={styles.builderInput} value={leg.subjectValue}
          onChangeText={v => onUpdate({ subjectValue: v })}
          placeholder="e.g. Lakers, Chiefs…" placeholderTextColor="rgba(255,255,255,0.25)" />
      ) : (
        <View style={{ marginBottom: 8 }}>
          <TextInput style={styles.builderInput} value={leg.playerSearch}
            onChangeText={v => onUpdate({ playerSearch: v, subjectValue: v })}
            placeholder="Search or type player name…" placeholderTextColor="rgba(255,255,255,0.25)" />
          {playersLoading ? (
            <ActivityIndicator color="#a78bfa" size="small" style={{ marginTop: 4 }} />
          ) : filteredPlayers.length > 0 && leg.playerSearch ? (
            <View style={[styles.playerList, { maxHeight: 120 }]}>
              {filteredPlayers.slice(0, 8).map((p, i) => {
                const name = typeof p === 'string' ? p : (p?.player_name || p?.name || '');
                if (!name) return null;
                return (
                  <TouchableOpacity key={i} onPress={() => onUpdate({ subjectValue: name, playerSearch: name })}
                    style={[styles.playerRow, leg.subjectValue === name && styles.playerRowActive]}>
                    <Text style={[styles.playerName, leg.subjectValue === name && { color: '#e879f9' }]}>{name}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          ) : null}
        </View>
      )}

      {/* Market */}
      <Text style={styles.legFieldLabel}>Market</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 8 }} contentContainerStyle={{ gap: 5 }}>
        {marketsForSport.slice(0, 20).map(m => (
          <Pill key={m} label={m} active={leg.market === m} onPress={() => onUpdate({ market: leg.market === m ? null : m })}
            color="#fbbf24" small />
        ))}
      </ScrollView>

      {/* Direction + Line */}
      <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
        <View style={{ flexDirection: 'row', gap: 5 }}>
          {['Over', 'Under', 'N/A'].map(d => (
            <Pill key={d} label={d} active={leg.direction === d} small
              onPress={() => onUpdate({ direction: leg.direction === d ? null : d })}
              color={d === 'Over' ? '#4ade80' : d === 'Under' ? '#f87171' : 'rgba(255,255,255,0.35)'} />
          ))}
        </View>
        {leg.direction && leg.direction !== 'N/A' && (
          <TextInput style={[styles.builderInput, { width: 90, marginBottom: 0, flexShrink: 1 }]}
            value={leg.lineValue} onChangeText={v => onUpdate({ lineValue: v })}
            keyboardType="numeric" placeholder="e.g. 25.5"
            placeholderTextColor="rgba(255,255,255,0.25)" />
        )}
      </View>
    </View>
  );
}

function QueryBuilderTab({ onRunQuery }) {
  const [mode, setMode] = useState(null);
  const [sport, setSport] = useState(null);
  const [league, setLeague] = useState(null);
  const [teamContext, setTeamContext] = useState(''); // for SGP: the game/team context

  // Single-leg state (Straight / Live)
  const [subjectType, setSubjectType] = useState('team');
  const [subjectValue, setSubjectValue] = useState('');
  const [market, setMarket] = useState(null);
  const [direction, setDirection] = useState(null);
  const [lineValue, setLineValue] = useState('');
  const [playerSearch, setPlayerSearch] = useState('');

  // Multi-leg state (SGP)
  const [legs, setLegs] = useState([EMPTY_LEG(1)]);
  const addLeg = () => setLegs(l => [...l, EMPTY_LEG(Date.now())]);
  const removeLeg = (id) => setLegs(l => l.filter(leg => leg.id !== id));
  const updateLeg = (id, updates) => setLegs(l => l.map(leg => leg.id === id ? { ...leg, ...updates } : leg));

  // Markets + players
  const [marketsData, setMarketsData] = useState(null);
  const [marketsLoading, setMarketsLoading] = useState(false);
  const [players, setPlayers] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(false);

  useEffect(() => {
    const base = getFunctionsBase();
    if (!base || marketsData) return;
    setMarketsLoading(true);
    fetch(`${base}/getMarketsData`).then(r => r.json())
      .then(d => setMarketsData(d?.sportToMarkets || {}))
      .catch(() => {}).finally(() => setMarketsLoading(false));
  }, []);

  useEffect(() => {
    if (!league) return;
    const base = getFunctionsBase();
    if (!base) return;
    setPlayersLoading(true);
    setPlayers([]);
    fetch(`${base}/listPlayerProps?limit=200&league=${encodeURIComponent(league)}`)
      .then(r => r.json())
      .then(d => { const list = Array.isArray(d) ? d : (d?.players || d?.data || []); setPlayers(list.slice(0, 200)); })
      .catch(() => {}).finally(() => setPlayersLoading(false));
  }, [league]);

  const leaguesForSport = useMemo(() => {
    if (!sport) return [];
    return SPORTS_LEAGUES.find(s => s.sport === sport)?.leagues || [];
  }, [sport]);

  const marketsForSport = useMemo(() => {
    if (!sport || !marketsData) return [];
    const key = Object.keys(marketsData).find(k => k.toLowerCase() === sport.toLowerCase());
    return key ? (marketsData[key] || []) : [];
  }, [sport, marketsData]);

  const filteredPlayers = useMemo(() => {
    if (!playerSearch.trim()) return players.slice(0, 50);
    const q = playerSearch.toLowerCase();
    return players.filter(p => {
      const name = typeof p === 'string' ? p : (p?.player_name || p?.name || '');
      return name.toLowerCase().includes(q);
    }).slice(0, 50);
  }, [players, playerSearch]);

  const builtQuery = useMemo(() => {
    if (mode === 'SGP') {
      const legStrings = legs.map(leg => {
        const parts = [];
        if (leg.subjectValue) parts.push(leg.subjectValue.trim());
        if (leg.direction && leg.direction !== 'N/A' && leg.lineValue) parts.push(`${leg.direction.toLowerCase()} ${leg.lineValue}`);
        else if (leg.lineValue) parts.push(`${leg.lineValue}+`);
        if (leg.market) parts.push(leg.market.toLowerCase());
        return parts.join(' ');
      }).filter(Boolean);

      const prefix = teamContext.trim();
      const combined = legStrings.join(' and ');
      if (!combined) return prefix ? `${prefix} sgp` : '';
      return prefix ? `${prefix} sgp ${combined}` : `${combined} sgp`;
    }

    const parts = [];
    if (mode === 'Live') parts.push('live');
    if (subjectType === 'player' && subjectValue) {
      parts.push(subjectValue.trim());
      if (direction && direction !== 'N/A' && lineValue) parts.push(`${direction.toLowerCase()} ${lineValue}`);
      else if (lineValue) parts.push(`${lineValue}+`);
      if (market) parts.push(market.toLowerCase());
    } else {
      if (subjectValue) parts.push(subjectValue.trim());
      if (market) parts.push(market.toLowerCase());
      if (direction && direction !== 'N/A' && lineValue) parts.push(`${direction.toLowerCase()} ${lineValue}`);
    }
    return parts.join(' ');
  }, [mode, teamContext, legs, subjectType, subjectValue, market, direction, lineValue]);

  const reset = () => {
    setMode(null); setSport(null); setLeague(null); setTeamContext('');
    setSubjectType('team'); setSubjectValue(''); setMarket(null);
    setDirection(null); setLineValue(''); setPlayerSearch('');
    setLegs([EMPTY_LEG(1)]);
  };

  const StepHeader = ({ num, label, complete }) => (
    <View style={styles.stepHeader}>
      <View style={[styles.stepNum, complete && styles.stepNumDone]}>
        {complete ? <Ionicons name="checkmark" size={11} color="#fff" /> : <Text style={styles.stepNumText}>{num}</Text>}
      </View>
      <Text style={[styles.stepLabel, complete && { color: 'rgba(255,255,255,0.5)' }]}>{label}</Text>
    </View>
  );

  return (
    <ScrollView style={styles.tabContent} contentContainerStyle={{ paddingBottom: 80 }} showsVerticalScrollIndicator={false}>
      {/* Live preview bar */}
      <View style={styles.previewBar}>
        <Text style={styles.previewLabel}>Query Preview</Text>
        <Text style={[styles.previewQuery, !builtQuery && { color: 'rgba(255,255,255,0.2)', fontStyle: 'italic' }]}>
          {builtQuery || 'build your query below…'}
        </Text>
        {builtQuery ? (
          <TouchableOpacity onPress={() => onRunQuery(builtQuery)} style={styles.executeBtn}>
            <Text style={styles.executeBtnText}>Search</Text>
            <Ionicons name="arrow-forward-circle" size={16} color="#0f0f17" style={{ marginLeft: 4 }} />
          </TouchableOpacity>
        ) : null}
      </View>

      {/* Step 1: Mode */}
      <View style={styles.builderStep}>
        <StepHeader num={1} label="Query Mode" complete={!!mode} />
        <View style={styles.pillRow}>
          {['Straight', 'SGP', 'Live'].map(m => (
            <Pill key={m} label={m} active={mode === m} onPress={() => setMode(mode === m ? null : m)}
              color={m === 'Straight' ? '#60a5fa' : m === 'SGP' ? '#a78bfa' : '#f87171'} />
          ))}
        </View>
        <Text style={styles.stepHint}>
          {mode === 'SGP' ? 'Build a same-game parlay by adding multiple legs below.'
           : mode === 'Live' ? 'Search in-game / live lines. Works with any other query.'
           : 'Look up moneylines, spreads, totals, or player props.'}
        </Text>
      </View>

      {/* Step 2: Sport */}
      <View style={styles.builderStep}>
        <StepHeader num={2} label="Sport" complete={!!sport} />
        <View style={styles.pillRow}>
          {SPORTS_LEAGUES.map(s => (
            <Pill key={s.sport} label={s.sport} active={sport === s.sport}
              onPress={() => { setSport(sport === s.sport ? null : s.sport); setLeague(null); setMarket(null); }}
              color={s.color} />
          ))}
        </View>
      </View>

      {/* Step 3: League */}
      {sport && leaguesForSport.length > 0 && (
        <View style={styles.builderStep}>
          <StepHeader num={3} label="League" complete={!!league} />
          <View style={styles.pillRow}>
            {leaguesForSport.map(l => (
              <Pill key={l.id} label={l.label} active={league === l.id}
                onPress={() => setLeague(league === l.id ? null : l.id)} color="#60a5fa" />
            ))}
          </View>
        </View>
      )}

      {/* SGP-specific: team context + multi-leg builder */}
      {mode === 'SGP' && sport && (
        <>
          <View style={styles.builderStep}>
            <StepHeader num={4} label="Game Context (optional)" complete={!!teamContext} />
            <TextInput style={styles.builderInput} value={teamContext}
              onChangeText={setTeamContext}
              placeholder="e.g. Lakers, Chiefs vs Eagles, Arsenal…"
              placeholderTextColor="rgba(255,255,255,0.25)" />
            <Text style={styles.stepHint}>The team or matchup this parlay is built around.</Text>
          </View>

          <View style={styles.builderStep}>
            <StepHeader num={5} label={`Parlay Legs (${legs.length})`} complete={legs.some(l => l.subjectValue)} />
            <View style={{ gap: 10 }}>
              {legs.map((leg, i) => (
                <LegBuilder key={leg.id} leg={leg} index={i} total={legs.length}
                  players={players} playersLoading={playersLoading}
                  marketsForSport={marketsForSport}
                  onUpdate={(updates) => updateLeg(leg.id, updates)}
                  onRemove={() => removeLeg(leg.id)}
                  canRemove={legs.length > 1} />
              ))}
            </View>
            {legs.length < 6 && (
              <TouchableOpacity onPress={addLeg} style={styles.addLegBtn}>
                <Ionicons name="add-circle-outline" size={16} color="#a78bfa" />
                <Text style={styles.addLegBtnText}>Add Leg ({legs.length}/6)</Text>
              </TouchableOpacity>
            )}
          </View>
        </>
      )}

      {/* Non-SGP: Team or Player */}
      {mode !== 'SGP' && sport && (
        <View style={styles.builderStep}>
          <StepHeader num={4} label="Team or Player" complete={!!subjectValue} />
          <View style={{ flexDirection: 'row', gap: 8, marginBottom: 10 }}>
            <Pill label="Team / Matchup" active={subjectType === 'team'} onPress={() => { setSubjectType('team'); setSubjectValue(''); }} color="#34d399" />
            <Pill label="Player" active={subjectType === 'player'} onPress={() => { setSubjectType('player'); setSubjectValue(''); setPlayerSearch(''); }} color="#e879f9" />
          </View>

          {subjectType === 'team' ? (
            <TextInput style={styles.builderInput} value={subjectValue} onChangeText={setSubjectValue}
              placeholder="e.g. Lakers, Chiefs, Arsenal, Celtics vs Bucks…"
              placeholderTextColor="rgba(255,255,255,0.25)" />
          ) : (
            <View>
              <TextInput style={styles.builderInput} value={playerSearch} onChangeText={setPlayerSearch}
                placeholder="Search player name…" placeholderTextColor="rgba(255,255,255,0.25)" />
              {playersLoading ? (
                <ActivityIndicator color="#a78bfa" style={{ marginTop: 8 }} />
              ) : !league ? (
                <Text style={styles.stepHint}>Select a league above to load players.</Text>
              ) : filteredPlayers.length > 0 ? (
                <View style={styles.playerList}>
                  {filteredPlayers.map((p, i) => {
                    const name = typeof p === 'string' ? p : (p?.player_name || p?.name || '');
                    if (!name) return null;
                    return (
                      <TouchableOpacity key={i} onPress={() => { setSubjectValue(name); setPlayerSearch(name); }}
                        style={[styles.playerRow, subjectValue === name && styles.playerRowActive]}>
                        <Text style={[styles.playerName, subjectValue === name && { color: '#e879f9' }]}>{name}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              ) : playerSearch.length > 0 ? (
                <Text style={styles.stepHint}>No players found. Type their full name to use them anyway.</Text>
              ) : (
                <Text style={styles.stepHint}>No players loaded for this league yet. Type a name manually.</Text>
              )}
              {playerSearch && !filteredPlayers.find(p => (typeof p === 'string' ? p : (p?.player_name || p?.name || '')) === playerSearch) && (
                <TouchableOpacity onPress={() => setSubjectValue(playerSearch)}
                  style={{ marginTop: 6, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Ionicons name="add-circle-outline" size={14} color="#a78bfa" />
                  <Text style={{ color: '#a78bfa', fontSize: 12 }}>Use "{playerSearch}"</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>
      )}

      {/* Market (non-SGP) */}
      {mode !== 'SGP' && sport && (
        <View style={styles.builderStep}>
          <StepHeader num={5} label="Market" complete={!!market} />
          {marketsLoading ? (
            <ActivityIndicator color="#a78bfa" style={{ margin: 8 }} />
          ) : marketsForSport.length > 0 ? (
            <View style={styles.pillRow}>
              {marketsForSport.slice(0, 30).map(m => (
                <Pill key={m} label={m} active={market === m} onPress={() => setMarket(market === m ? null : m)}
                  color="#fbbf24" small />
              ))}
              {marketsForSport.length > 30 && (
                <Text style={styles.stepHint}>+{marketsForSport.length - 30} more — check the Markets tab</Text>
              )}
            </View>
          ) : (
            <Text style={styles.stepHint}>Select a sport above to see available markets.</Text>
          )}
        </View>
      )}

      {/* Direction + Line (non-SGP) */}
      {mode !== 'SGP' && (
        <>
          <View style={styles.builderStep}>
            <StepHeader num={6} label="Direction" complete={!!direction} />
            <View style={styles.pillRow}>
              {['Over', 'Under', 'N/A'].map(d => (
                <Pill key={d} label={d} active={direction === d} onPress={() => setDirection(direction === d ? null : d)}
                  color={d === 'Over' ? '#4ade80' : d === 'Under' ? '#f87171' : 'rgba(255,255,255,0.4)'} />
              ))}
            </View>
            <Text style={styles.stepHint}>For player props and totals. Use N/A for moneylines and spreads.</Text>
          </View>

          {direction && direction !== 'N/A' && (
            <View style={styles.builderStep}>
              <StepHeader num={7} label="Line Value" complete={!!lineValue} />
              <TextInput style={[styles.builderInput, { width: 140 }]} value={lineValue} onChangeText={setLineValue}
                keyboardType="numeric" placeholder="e.g. 25.5, 220, 300"
                placeholderTextColor="rgba(255,255,255,0.25)" />
            </View>
          )}
        </>
      )}

      {/* Reset */}
      <TouchableOpacity onPress={reset} style={styles.resetBtn}>
        <Ionicons name="refresh-outline" size={14} color="rgba(255,255,255,0.4)" />
        <Text style={styles.resetBtnText}>Reset Builder</Text>
      </TouchableOpacity>
    </ScrollView>
  );
}

// ─── Tab 3: Markets ───────────────────────────────────────────────────────────
function MarketsTab() {
  const [marketsData, setMarketsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSport, setSelectedSport] = useState(null);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const base = getFunctionsBase();
    if (!base) { setLoading(false); return; }
    fetch(`${base}/getMarketsData`)
      .then(r => r.json())
      .then(d => {
        setMarketsData(d?.sportToMarkets || {});
        const firstSport = Object.keys(d?.sportToMarkets || {})[0];
        if (firstSport) setSelectedSport(firstSport);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const sports = useMemo(() => (marketsData ? Object.keys(marketsData) : []), [marketsData]);

  const displayMarkets = useMemo(() => {
    if (!marketsData || !selectedSport) return [];
    const all = marketsData[selectedSport] || [];
    const q = search.trim().toLowerCase();
    const filtered = q ? all.filter(m => m.toLowerCase().includes(q)) : all;
    return showAll ? filtered : filtered.slice(0, 60);
  }, [marketsData, selectedSport, search, showAll]);

  const playerMarkets = displayMarkets.filter(m => m.toLowerCase().startsWith('player') || m.toLowerCase().startsWith('pitcher'));
  const teamMarkets = displayMarkets.filter(m => !m.toLowerCase().startsWith('player') && !m.toLowerCase().startsWith('pitcher'));

  if (loading) return <View style={styles.centered}><ActivityIndicator color="#a78bfa" size="large" /><Text style={styles.loadingText}>Loading markets…</Text></View>;

  if (!marketsData) return <View style={styles.centered}><Text style={styles.emptyText}>Could not load markets data.</Text></View>;

  return (
    <View style={{ flex: 1 }}>
      {/* Sport tabs */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.sportTabBar} contentContainerStyle={{ gap: 6, paddingHorizontal: 16 }}>
        {sports.map(s => (
          <TouchableOpacity key={s} onPress={() => { setSelectedSport(s); setSearch(''); setShowAll(false); }}
            style={[styles.sportTab, selectedSport === s && styles.sportTabActive]}>
            <Text style={[styles.sportTabText, selectedSport === s && styles.sportTabTextActive]}>{s}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Search */}
      <View style={{ paddingHorizontal: 16, paddingVertical: 10 }}>
        <TextInput
          style={styles.searchInput}
          value={search}
          onChangeText={v => { setSearch(v); setShowAll(false); }}
          placeholder="Search markets…"
          placeholderTextColor="rgba(255,255,255,0.3)"
        />
      </View>

      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 60 }} showsVerticalScrollIndicator={false}>
        {teamMarkets.length > 0 && (
          <>
            <Text style={styles.marketSection}>Team / Game Markets ({teamMarkets.length})</Text>
            <View style={{ gap: 6 }}>
              {teamMarkets.map((m, i) => {
                const desc = MARKET_DESCRIPTIONS[m];
                return desc ? (
                  <View key={i} style={styles.marketDescCard}>
                    <Text style={styles.marketDescName}>{m}</Text>
                    <Text style={styles.marketDescText}>{desc.desc}</Text>
                    {desc.shortcut && <Text style={styles.marketDescShortcut}>Query: {desc.shortcut}</Text>}
                  </View>
                ) : (
                  <View key={i} style={styles.marketBadge}><Text style={styles.marketBadgeText}>{m}</Text></View>
                );
              })}
            </View>
          </>
        )}
        {playerMarkets.length > 0 && (
          <>
            <Text style={[styles.marketSection, { marginTop: 16 }]}>Player Prop Markets ({playerMarkets.length})</Text>
            <View style={{ gap: 6 }}>
              {playerMarkets.map((m, i) => {
                const desc = MARKET_DESCRIPTIONS[m];
                return desc ? (
                  <View key={i} style={[styles.marketDescCard, { borderColor: 'rgba(232,121,249,0.2)', backgroundColor: 'rgba(232,121,249,0.05)' }]}>
                    <Text style={[styles.marketDescName, { color: '#e879f9' }]}>{m}</Text>
                    <Text style={styles.marketDescText}>{desc.desc}</Text>
                    {desc.shortcut && <Text style={[styles.marketDescShortcut, { color: 'rgba(232,121,249,0.6)' }]}>Query: {desc.shortcut}</Text>}
                  </View>
                ) : (
                  <View key={i} style={[styles.marketBadge, { borderColor: 'rgba(232,121,249,0.3)', backgroundColor: 'rgba(232,121,249,0.06)' }]}>
                    <Text style={[styles.marketBadgeText, { color: '#e879f9' }]}>{m}</Text>
                  </View>
                );
              })}
            </View>
          </>
        )}
        {!showAll && marketsData && selectedSport && (marketsData[selectedSport] || []).length > 60 && (
          <TouchableOpacity onPress={() => setShowAll(true)} style={styles.showMoreBtn}>
            <Text style={styles.showMoreText}>Show all {(marketsData[selectedSport] || []).length} markets</Text>
          </TouchableOpacity>
        )}
        {displayMarkets.length === 0 && (
          <Text style={styles.emptyText}>No markets match "{search}"</Text>
        )}
      </ScrollView>
    </View>
  );
}

// ─── Tab 4: Players ───────────────────────────────────────────────────────────
const LIST_PAGE_SIZE = 50;

// Shared hook — loads a paginated, searchable list from a docs endpoint
function useDocsList(endpoint, league, responseKey) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');

  const load = useCallback((lg) => {
    const base = getFunctionsBase();
    if (!base || !lg) return;
    setLoading(true);
    setItems([]);
    setPage(0);
    setSearch('');
    fetch(`${base}/${endpoint}?league=${encodeURIComponent(lg.toLowerCase())}`)
      .then(r => r.json())
      .then(d => {
        const raw = d?.[responseKey] || [];
        const seen = new Set();
        setItems(raw.filter(p => {
          const name = (p?.name || p?.player_name || '').trim();
          if (!name || seen.has(name.toLowerCase())) return false;
          seen.add(name.toLowerCase());
          return true;
        }));
      })
      .catch(err => console.warn(`[${endpoint}]`, err.message))
      .finally(() => setLoading(false));
  }, [endpoint, responseKey]);

  useEffect(() => { setPage(0); }, [search]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(p => {
      const name = p?.name || p?.player_name || '';
      const team = p?.team || '';
      return name.toLowerCase().includes(q) || team.toLowerCase().includes(q);
    });
  }, [items, search]);

  const visibleCount = (page + 1) * LIST_PAGE_SIZE;
  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;
  const loadMore = () => setPage(p => p + 1);

  return { items, loading, search, setSearch, filtered, visible, hasMore, loadMore, load };
}

// Injury status pill colours
const INJURY_COLORS = {
  out:          { bg: 'rgba(239,68,68,0.15)',   border: 'rgba(239,68,68,0.4)',   text: '#f87171', label: 'OUT' },
  doubtful:     { bg: 'rgba(249,115,22,0.15)',  border: 'rgba(249,115,22,0.4)',  text: '#fb923c', label: 'DTF' },
  questionable: { bg: 'rgba(234,179,8,0.15)',   border: 'rgba(234,179,8,0.4)',   text: '#fbbf24', label: '?' },
  probable:     { bg: 'rgba(34,197,94,0.1)',    border: 'rgba(34,197,94,0.3)',   text: '#4ade80', label: 'PROB' },
  active:       { bg: 'rgba(34,197,94,0.08)',   border: 'rgba(34,197,94,0.2)',   text: '#4ade80', label: 'ACT' },
};

function InjuryTag({ status }) {
  if (!status) return null;
  const key = (status || '').toLowerCase();
  const cfg = INJURY_COLORS[key] || { bg: 'rgba(255,255,255,0.07)', border: 'rgba(255,255,255,0.15)', text: 'rgba(255,255,255,0.5)', label: status.toUpperCase() };
  return (
    <View style={{ paddingHorizontal: 5, paddingVertical: 2, borderRadius: 5, borderWidth: 1, borderColor: cfg.border, backgroundColor: cfg.bg }}>
      <Text style={{ fontSize: 9, fontWeight: '700', color: cfg.text, letterSpacing: 0.4 }}>{cfg.label}</Text>
    </View>
  );
}

// ─── Tab 4: Players & Teams ───────────────────────────────────────────────────
function PlayersAndTeamsTab({ onRunQuery }) {
  const [selectedLeague, setSelectedLeague] = useState('nba');
  const [search, setSearch] = useState('');
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedTeams, setExpandedTeams] = useState(new Set());

  const allLeagues = useMemo(() =>
    SPORTS_LEAGUES.flatMap(s => s.leagues.filter(l =>
      l.type === 'team' || ['ufc', 'atp', 'wta', 'pga', 'boxing'].includes(l.id)
    )), []);

  const loadLeague = useCallback((league) => {
    const base = getFunctionsBase();
    if (!base) return;
    setLoading(true);
    setPlayers([]);
    setExpandedTeams(new Set());
    setSearch('');
    fetch(`${base}/getPlayersForDocs?league=${encodeURIComponent(league.toLowerCase())}`)
      .then(r => r.json())
      .then(d => {
        const raw = d?.players || [];
        const seen = new Set();
        setPlayers(raw.filter(p => {
          const name = (p?.name || '').trim();
          if (!name || seen.has(name.toLowerCase())) return false;
          seen.add(name.toLowerCase());
          return true;
        }));
      })
      .catch(err => console.warn('[PlayersAndTeamsTab]', err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadLeague(selectedLeague); }, [selectedLeague]);

  // Group players by team, client-side
  const teamMap = useMemo(() => {
    const map = new Map();
    players.forEach(p => {
      const team = (p.team || 'Unknown').trim();
      if (!map.has(team)) map.set(team, []);
      map.get(team).push(p);
    });
    // Sort teams alphabetically
    return new Map([...map.entries()].sort((a, b) => a[0].localeCompare(b[0])));
  }, [players]);

  // When searching, flatten and filter players; show their team inline
  const searchResults = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.trim().toLowerCase();
    return players.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.team || '').toLowerCase().includes(q)
    );
  }, [players, search]);

  const toggleTeam = (team) => {
    setExpandedTeams(prev => {
      const next = new Set(prev);
      next.has(team) ? next.delete(team) : next.add(team);
      return next;
    });
  };

  const teams = [...teamMap.keys()];

  return (
    <View style={{ flex: 1 }}>
      {/* League pills */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.sportTabBar} contentContainerStyle={{ gap: 6, paddingHorizontal: 16 }}>
        {allLeagues.map(l => (
          <TouchableOpacity key={l.id} onPress={() => setSelectedLeague(l.id)}
            style={[styles.sportTab, selectedLeague === l.id && styles.sportTabActive]}>
            <Text style={[styles.sportTabText, selectedLeague === l.id && styles.sportTabTextActive]}>{l.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Search */}
      <View style={{ paddingHorizontal: 16, paddingTop: 10, paddingBottom: 6 }}>
        <TextInput style={styles.searchInput} value={search} onChangeText={setSearch}
          placeholder="Search player or team…" placeholderTextColor="rgba(255,255,255,0.3)" />
        {!loading && players.length > 0 && (
          <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, marginTop: 5 }}>
            {search
              ? `${(searchResults || []).length} result${(searchResults || []).length !== 1 ? 's' : ''}`
              : `${teams.length} teams · ${players.length} players`}
          </Text>
        )}
      </View>

      {loading ? (
        <View style={styles.centered}>
          <ActivityIndicator color="#a78bfa" size="large" />
          <Text style={styles.loadingText}>Loading roster…</Text>
        </View>
      ) : (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 60 }} showsVerticalScrollIndicator={false}>

          {/* ── Search results (flat list) ── */}
          {searchResults !== null && (
            searchResults.length === 0
              ? <View style={{ alignItems: 'center', marginTop: 40 }}>
                  <Ionicons name="search-outline" size={28} color="rgba(255,255,255,0.15)" style={{ marginBottom: 8 }} />
                  <Text style={styles.emptyText}>No results for "{search}"</Text>
                </View>
              : searchResults.map((p, i) => (
                  <PlayerRow key={i} player={p} showTeam onRunQuery={onRunQuery} />
                ))
          )}

          {/* ── Team accordion list ── */}
          {searchResults === null && teams.length === 0 && (
            <View style={{ alignItems: 'center', marginTop: 40 }}>
              <Ionicons name="shield-outline" size={28} color="rgba(255,255,255,0.15)" style={{ marginBottom: 8 }} />
              <Text style={styles.emptyText}>No roster data for this league yet.</Text>
            </View>
          )}

          {searchResults === null && teams.map(team => {
            const roster = teamMap.get(team) || [];
            const expanded = expandedTeams.has(team);
            const injuredCount = roster.filter(p => p.is_injured).length;

            return (
              <View key={team} style={styles.teamAccordion}>
                {/* Team header row */}
                <TouchableOpacity onPress={() => toggleTeam(team)} style={styles.teamAccordionHeader} activeOpacity={0.7}>
                  <View style={styles.teamAccordionLeft}>
                    <Ionicons name="shield-outline" size={15} color="#60a5fa" />
                    <Text style={styles.teamAccordionName}>{team}</Text>
                    {injuredCount > 0 && (
                      <View style={styles.injuredBadge}>
                        <Ionicons name="medkit-outline" size={9} color="#f87171" />
                        <Text style={styles.injuredBadgeText}>{injuredCount}</Text>
                      </View>
                    )}
                  </View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Text style={styles.teamRosterCount}>{roster.length} players</Text>
                    <TouchableOpacity onPress={() => onRunQuery(`${team} moneyline`)} style={styles.teamMLBtn}>
                      <Text style={styles.teamMLBtnText}>ML</Text>
                    </TouchableOpacity>
                    <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={14} color="rgba(255,255,255,0.35)" />
                  </View>
                </TouchableOpacity>

                {/* Roster */}
                {expanded && (
                  <View style={styles.rosterContainer}>
                    {roster.map((p, i) => (
                      <PlayerRow key={i} player={p} onRunQuery={onRunQuery} />
                    ))}
                  </View>
                )}
              </View>
            );
          })}
        </ScrollView>
      )}
    </View>
  );
}

function PlayerRow({ player, showTeam, onRunQuery }) {
  const name = player?.name || '';
  const pos  = player?.position || '';
  const team = player?.team || '';
  const injuryStatus = player?.injury_status;
  const isInjured = player?.is_injured;
  const injuryDetails = player?.injury_details;

  return (
    <TouchableOpacity onPress={() => onRunQuery(`${name} props`)} style={styles.playerRow} activeOpacity={0.7}>
      <View style={styles.playerRowLeft}>
        {pos ? <Text style={styles.playerRowPos}>{pos}</Text> : null}
        <View style={{ flex: 1 }}>
          <Text style={styles.playerRowName}>{name}</Text>
          {showTeam && team ? <Text style={styles.playerRowSub}>{team}</Text> : null}
          {injuryDetails ? <Text style={styles.playerRowInjuryDetail} numberOfLines={1}>{typeof injuryDetails === 'string' ? injuryDetails : (injuryDetails?.description || injuryDetails?.body_part || '')}</Text> : null}
        </View>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        {(isInjured || injuryStatus) && <InjuryTag status={injuryStatus || 'out'} />}
        <Ionicons name="chevron-forward" size={12} color="rgba(167,139,250,0.4)" />
      </View>
    </TouchableOpacity>
  );
}

// ─── Tab 5: Books ─────────────────────────────────────────────────────────────
function BooksTab() {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return SPORTSBOOK_TIERS;
    return SPORTSBOOK_TIERS.map(tier => ({
      ...tier,
      books: tier.books.filter(b => b.label.toLowerCase().includes(q) || b.id.toLowerCase().includes(q)),
    })).filter(t => t.books.length > 0);
  }, [search]);

  return (
    <ScrollView style={styles.tabContent} contentContainerStyle={{ paddingBottom: 60 }} showsVerticalScrollIndicator={false}>
      <View style={{ paddingHorizontal: 0, paddingBottom: 10 }}>
        <TextInput
          style={styles.searchInput}
          value={search}
          onChangeText={setSearch}
          placeholder="Search sportsbooks…"
          placeholderTextColor="rgba(255,255,255,0.3)"
        />
      </View>

      {filtered.map(tier => (
        <View key={tier.tier} style={styles.section}>
          <View style={[styles.tierHeader, { borderLeftColor: tier.color }]}>
            <View style={[styles.tierBadge, { backgroundColor: tier.color + '22', borderColor: tier.color + '55' }]}>
              <Text style={[styles.tierBadgeText, { color: tier.color }]}>Tier {tier.tier}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.tierLabel}>{tier.label}</Text>
              <Text style={styles.tierDesc}>{tier.description}</Text>
            </View>
          </View>
          <View style={styles.bookGrid}>
            {tier.books.map(b => (
              <View key={b.id} style={[styles.bookPill, { borderColor: tier.color + '33' }]}>
                <Text style={[styles.bookPillText, { color: tier.color }]}>{b.label}</Text>
              </View>
            ))}
          </View>
        </View>
      ))}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Using Sportsbooks in Queries</Text>
        <View style={styles.tableCard}>
          {[
            { pattern: 'best bets on [book]', example: 'best bets on draftkings' },
            { pattern: '[book] [league] bets', example: 'fanduel nba best bets' },
            { pattern: '[book] best picks tonight', example: 'caesars best picks tonight' },
          ].map((row, i) => (
            <View key={i} style={[styles.tableRow, i < 2 && styles.tableRowBorder]}>
              <Text style={[styles.tablePattern, { flex: 1 }]}>{row.pattern}</Text>
              <Text style={[styles.exampleQuery, { color: '#60a5fa', fontSize: 12 }]}>{row.example}</Text>
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );
}

// ─── Tab 6: Glossary ─────────────────────────────────────────────────────────
function GlossaryTab() {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const categories = useMemo(() => {
    const q = search.trim().toLowerCase();
    const terms = q
      ? GLOSSARY_TERMS.filter(t =>
          t.term.toLowerCase().includes(q) ||
          t.definition.toLowerCase().includes(q) ||
          t.category.toLowerCase().includes(q))
      : GLOSSARY_TERMS;

    const grouped = {};
    terms.forEach(t => {
      if (!grouped[t.category]) grouped[t.category] = [];
      grouped[t.category].push(t);
    });
    return grouped;
  }, [search]);

  const CATEGORY_COLORS = {
    'Bet Types': '#60a5fa',
    'Math & Odds': '#a78bfa',
    'Odds Formats': '#fbbf24',
    'Platform': '#34d399',
    'Betting Slang': '#f97316',
    'Abbreviations': '#e879f9',
  };

  return (
    <ScrollView style={styles.tabContent} contentContainerStyle={{ paddingBottom: 60 }} showsVerticalScrollIndicator={false}>
      <View style={{ marginBottom: 16 }}>
        <TextInput
          style={styles.searchInput}
          value={search}
          onChangeText={setSearch}
          placeholder="Search terms…"
          placeholderTextColor="rgba(255,255,255,0.3)"
        />
      </View>

      {Object.keys(categories).length === 0 && (
        <Text style={styles.emptyText}>No terms match "{search}"</Text>
      )}

      {Object.entries(categories).map(([cat, terms]) => {
        const catColor = CATEGORY_COLORS[cat] || '#a78bfa';
        return (
          <View key={cat} style={styles.section}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <View style={[styles.tierBadge, { backgroundColor: catColor + '22', borderColor: catColor + '55' }]}>
                <Text style={[styles.tierBadgeText, { color: catColor }]}>{cat}</Text>
              </View>
              <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>{terms.length} terms</Text>
            </View>
            <View style={{ gap: 6 }}>
              {terms.map((term, i) => {
                const key = `${cat}-${i}`;
                const isOpen = expanded === key;
                return (
                  <TouchableOpacity key={key} onPress={() => setExpanded(isOpen ? null : key)} activeOpacity={0.85}>
                    <View style={[styles.glossaryCard, isOpen && { borderColor: catColor + '55', backgroundColor: catColor + '0a' }]}>
                      <View style={styles.glossaryHeader}>
                        <Text style={[styles.glossaryTerm, isOpen && { color: catColor }]}>{term.term}</Text>
                        <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={14} color="rgba(255,255,255,0.3)" />
                      </View>
                      {!isOpen && (
                        <Text style={styles.glossaryPreview} numberOfLines={1}>{term.definition}</Text>
                      )}
                      {isOpen && (
                        <View style={{ paddingTop: 8, gap: 8 }}>
                          <Text style={styles.glossaryDef}>{term.definition}</Text>
                          {term.example && (
                            <View style={styles.glossaryExample}>
                              <Ionicons name="code-working-outline" size={12} color={catColor} style={{ marginRight: 5, marginTop: 1 }} />
                              <Text style={[styles.glossaryExampleText, { color: catColor + 'cc', flex: 1 }]}>{term.example}</Text>
                            </View>
                          )}
                        </View>
                      )}
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        );
      })}
    </ScrollView>
  );
}

// ─── Main Screen ──────────────────────────────────────────────────────────────
export default function DocsScreen() {
  const { navigateTo, goBack } = useNavigation();
  const [activeTab, setActiveTab] = useState(0);
  const windowWidth = Dimensions.get('window').width;
  const isMobile = windowWidth < 768;

  const handleRunQuery = useCallback((query) => {
    if (typeof window !== 'undefined') {
      try { window.sessionStorage.setItem('sgp:lastQuery', query); } catch {}
    }
    navigateTo('SgpFlow', { payload: { query } });
  }, [navigateTo]);

  return (
    <View style={styles.root}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={goBack} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={20} color="rgba(255,255,255,0.7)" />
          <Text style={styles.backBtnText}>Back</Text>
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          <Text style={styles.headerTitle}>Documentation</Text>
          <Text style={styles.headerSub}>Query reference, builder, markets, players, and books</Text>
        </View>
        <TouchableOpacity onPress={() => handleRunQuery('')} style={styles.searchShortcut}>
          <Ionicons name="search-outline" size={16} color="#a78bfa" />
          <Text style={styles.searchShortcutText}>Search</Text>
        </TouchableOpacity>
      </View>

      {/* Tab bar */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabBar} contentContainerStyle={{ paddingHorizontal: 12 }}>
        {TABS.map((tab, i) => (
          <TouchableOpacity key={tab} onPress={() => setActiveTab(i)} style={[styles.tabBtn, activeTab === i && styles.tabBtnActive]}>
            <Ionicons name={TAB_ICONS[i]} size={14} color={activeTab === i ? '#a78bfa' : 'rgba(255,255,255,0.4)'} style={{ marginRight: 5 }} />
            <Text style={[styles.tabBtnText, activeTab === i && styles.tabBtnTextActive]}>{tab}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Content */}
      <View style={{ flex: 1 }}>
        {activeTab === 0 && <HowItWorksTab onRunQuery={handleRunQuery} />}
        {activeTab === 1 && <QueryBuilderTab onRunQuery={handleRunQuery} />}
        {activeTab === 2 && <MarketsTab />}
        {activeTab === 3 && <PlayersAndTeamsTab onRunQuery={handleRunQuery} />}
        {activeTab === 4 && <BooksTab />}
        {activeTab === 5 && <GlossaryTab />}
      </View>
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
